import React, { Fragment, useEffect, useState } from "react";
import styles from "./index.module.css";

import Auth from "shared/@auth";
import { useTopics } from "../useTopics";
import { useQueryClient } from "react-query";

import DataList, { DataRow } from "shared/DataList";
import { Section, Row, Column, LabelGroup } from "shared/layout";
import Link from "shared/Link";
import Button from "shared/Button";

import { ArrowRightTop } from "react-ikonate";
import dayjs from "dayjs";
import ReactModal from "react-modal";
import TabActionBar from "shared/TabActionBar";
import Loader from "shared/Loader";
import classNames from "classnames";

import AccessCodeModal from "../AccessCodeModal";
import TopicsHistory from "../TopicsHistory";
import TopicUsers from "../TopicUsers";

function getMemberUrl(iboNumber) {
  return `/membership/lookup/ibo/${iboNumber}`;
}

function hasIndices(arr) {
  if (Array.isArray(arr) && arr.length > 0) return true;
  return false;
}

export default function TopicDetails({
  data,
  refetch,
  syncTopicsAccount,
  fetchIboSyncData,
  hideHeader,
  retrieveGroupUsers,
  retrieveThreadUsers,
  retrieveAccessCode,
  accessCode,
  clearAccessCode,
  userData,
}) {
  const { hasPermission } = Auth.useContainer();

  const canViewAccessCode = hasPermission("topics.accesscode.create");

  const iboNumber = data?.wwgInfo?.iboNumber;
  const { cancelSyncPut } = useTopics(data.iboNumber);
  const [contact, setContact] = useState(data || {});
  const isAssociateIbo =
    contact?.los?.pinLevel?.name?.toLowerCase() === "associate ibo";
  const [isEditing, setIsEditing] = useState(false);
  const [editableContact, setEditableContact] = useState({
    firstName: data?.wwgInfo?.firstName,
    lastName: data?.wwgInfo?.lastName,
    email: data?.wwgInfo?.email,
  });

  const [isAccessOpen, setIsAccessOpen] = useState(false);
  useEffect(() => {
    if (accessCode) {
      setIsAccessOpen(true);
    }
  }, [accessCode]);

  const [loading, setLoading] = useState(false);
  const { updateContactInfo } = useTopics(data?.wwgInfo?.iboNumber);

  const organizationTypes = {
    ownOrganization: `${contact?.wwgInfo?.firstName} ${contact?.wwgInfo?.lastName} can send messages to:`,
    uplineOrganizations: `${contact?.wwgInfo?.firstName} ${contact?.wwgInfo?.lastName} can receive messages from:`,
    pluginOrganizations: "Plugged Into Organizations",
    otherOrganizations: "Other Organizations",
  };
  function orgPresent() {
    return (
      contact.topics &&
      // Check if ownOrganization has groups or threads
      ((contact.topics.ownOrganization &&
        (hasIndices(contact.topics.ownOrganization.groups) ||
          hasIndices(contact.topics.ownOrganization.threads))) ||
        hasIndices(contact.topics.uplineOrganizations) ||
        hasIndices(contact.topics.pluginOrganizations) ||
        hasIndices(contact.topics.otherOrganizations))
    );
  }

  useEffect(() => {
    if (data) {
      setContact(data);
      setEditableContact({
        firstName: data?.wwgInfo?.firstName || "",
        lastName: data?.wwgInfo?.lastName || "",
        email: data?.wwgInfo?.email || "",
      });
    }
  }, [data]);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isShowHistory, setIsShowHistory] = useState(false);

  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isUsersGroup, setIsUsersGroup] = useState(false);
  const [isUsersHeader, setIsUsersHeader] = useState("");

  const [iboSyncData, setIboSyncData] = useState(null);
  const queryClient = useQueryClient();
  const handleCancel = () => {
    cancelSyncPut.mutate({ syncId: iboSyncData._id });
  };
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    const hasChanged =
      editableContact.firstName !== contact?.wwgInfo?.firstName ||
      editableContact.lastName !== contact?.wwgInfo?.lastName ||
      editableContact.email !== contact?.wwgInfo?.email;

    const isValid = validateFields();
    setIsSaveDisabled(!hasChanged || !isValid); // Disable if no changes or invalid fields
  }, [editableContact, contact]);
  const handleEditChange = (field, value) => {
    setEditableContact((prev) => ({ ...prev, [field]: value }));

    // Validate field on change
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (field === "firstName" && value.trim()) {
        delete updatedErrors.firstName;
      }
      if (field === "lastName" && value.trim()) {
        delete updatedErrors.lastName;
      }
      if (field === "email") {
        if (!value.trim()) {
          updatedErrors.email = "Email cannot be blank.";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          updatedErrors.email = "Email is not in a valid format.";
        } else {
          delete updatedErrors.email;
        }
      }
      return updatedErrors;
    });

    // Check if save button should be enabled
    const hasChanged =
      value !== contact.wwgInfo[field] ||
      editableContact.firstName !== contact?.wwgInfo?.firstName ||
      editableContact.lastName !== contact?.wwgInfo?.lastName ||
      editableContact.email !== contact?.wwgInfo?.email;
    setIsSaveDisabled(!hasChanged || Object.keys(errors).length > 0);
  };
  const handleSave = async () => {
    if (isSaveDisabled) return;
    if (!validateFields()) return;

    setLoading(true);
    setIsSaveDisabled(true);

    try {
      const response = await updateContactInfo.mutateAsync({
        iboNumber: contact.wwgInfo?.iboNumber,
        wwgId: contact.wwgInfo?.wwgId,
        firstName: editableContact.firstName,
        lastName: editableContact.lastName,
        email: editableContact.email,
      });

      if (response.status === 204) {
        const updatedData = await refetch();

        const selectedIbo = updatedData?.iboA;

        if (!selectedIbo) {
          throw new Error("No valid data found for iboA after refetch.");
        }

        setContact(selectedIbo);
        setEditableContact({
          firstName: selectedIbo?.wwgInfo?.firstName || "",
          lastName: selectedIbo?.wwgInfo?.lastName || "",
          email: selectedIbo?.wwgInfo?.email || "",
        });
        setIsEditing(false);
        setErrors({}); // Clear errors
      }
    } catch (error) {
      console.error("Error saving or refetching:", error);

      if (error.message.includes("Email already exists")) {
        console.warn("Setting email error:", error.message);
        setErrors((prev) => ({
          ...prev,
          email: "This email is already in use.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: "An unexpected error occurred. Please try again.",
        }));
      }

      setIsEditing(true);
    } finally {
      setLoading(false);
      setIsSaveDisabled(false);
    }
  };

  const handleCancelEdit = () => {
    setEditableContact({
      firstName: contact.wwgInfo.firstName,
      lastName: contact.wwgInfo.lastName,
      email: contact.wwgInfo.email,
    });
    setIsEditing(false);
  };

  const [errors, setErrors] = useState({});
  const validateFields = () => {
    const newErrors = {};
    if (!editableContact.firstName.trim()) {
      newErrors.firstName = "First name cannot be blank.";
    }
    if (!editableContact.lastName.trim()) {
      newErrors.lastName = "Last name cannot be blank.";
    }
    if (!editableContact.email.trim()) {
      newErrors.email = "Email cannot be blank.";
    } else if (!/\S+@\S+\.\S+/.test(editableContact.email)) {
      newErrors.email = "Email is not in a valid format.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div>
      <ReactModal
        isOpen={isHistoryOpen}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnOverlayEsc={true}
        onRequestClose={() => {
          setIsHistoryOpen(false);
        }}
      >
        <TopicsHistory
          ibo={contact}
          isCreatingIbo={false}
          isShowHistory={isShowHistory}
          showMarkComplete={iboSyncData}
          syncTopicsAccount={syncTopicsAccount}
          refresh={async (contact) => {
            await refetch(contact);
            try {
              const data = await fetchIboSyncData(contact);
              setIboSyncData(data);
            } catch (error) {
              console.error("Error fetching data: ", error);
            }
          }}
          onClose={() => {
            setIsHistoryOpen(false);
            setIsShowHistory(false);
          }}
          onCancel={handleCancel}
        />
      </ReactModal>

      <ReactModal
        isOpen={isAccessOpen}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnOverlayEsc={true}
        onRequestClose={() => {
          setIsAccessOpen(false);
        }}
      >
        <AccessCodeModal
          onClose={() => {
            clearAccessCode();
            setIsAccessOpen(false);
          }}
          accessCode={accessCode}
        ></AccessCodeModal>
      </ReactModal>

      <ReactModal
        isOpen={isUsersOpen}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnOverlayEsc={false}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <TopicUsers
              isGroup={isUsersGroup}
              header={isUsersHeader}
              userData={userData}
              onClose={() => {
                setIsUsersOpen(false);
              }}
            />
          </>
        )}
      </ReactModal>

      <Section>
        {!hideHeader && (
          <TabActionBar>
            <header className={styles.tabActionBarHeader}>
              <h3>
                {contact?.wwgInfo?.firstName} {contact?.wwgInfo?.lastName} (
                {contact?.wwgInfo?.type})
              </h3>
              <div>
                <Button
                  size="small"
                  onClick={() => {
                    setIsHistoryOpen(!isHistoryOpen);
                    setIsShowHistory(true);
                  }}
                >
                  Show History
                </Button>
                {contact.wwgInfo.wwgId &&
                  (isAssociateIbo || canViewAccessCode) && (
                    <>
                      &nbsp;
                      <Button
                        size="small"
                        onClick={async () => {
                          await retrieveAccessCode(contact);
                        }}
                        purpose="save"
                      >
                        Access Code
                      </Button>
                    </>
                  )}
                &nbsp;
                <Button
                  size="small"
                  onClick={async () => {
                    if (!contact.isSyncing) {
                      setContact({ ...contact, actions: [], logs: [] });
                      await syncTopicsAccount(contact);
                    }
                    await refetch(contact);
                    setIsHistoryOpen(true);
                  }}
                  purpose="delete"
                >
                  Sync
                </Button>
              </div>
            </header>
          </TabActionBar>
        )}

        <Section>
          <header>
            <h3>Account Details</h3>
            {!isEditing ? (
              <Button
                size="small"
                className={styles.contactButton}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            ) : (
              <div className={styles.actions}>
                <Button
                  size="small"
                  onClick={handleSave}
                  disabled={isSaveDisabled || Object.keys(errors).length > 0}
                >
                  {loading ? <Loader size="small" /> : "Save"}
                </Button>
                <Button
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={loading} // Only disable during save
                >
                  Cancel
                </Button>
              </div>
            )}
          </header>

          {!isEditing ? (
            <Row>
              <DataList>
                <DataRow label="Name">
                  {contact?.wwgInfo?.firstName} {contact?.wwgInfo?.lastName}
                </DataRow>
                <DataRow label="Email">{contact?.wwgInfo?.email}</DataRow>
                <DataRow label="WWG ID">{contact?.wwgInfo?.wwgId}</DataRow>
                <DataRow label="Topics ID">
                  {contact?.topicsInfo?.topicsId.toString?.()}
                </DataRow>
              </DataList>
            </Row>
          ) : (
            <>
              <Row className={styles.splitGroup}>
                <div className={styles.inputGroup}>
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    value={editableContact.firstName}
                    onChange={(e) =>
                      handleEditChange("firstName", e.target.value)
                    }
                    disabled={loading}
                  />
                  {errors.firstName && (
                    <span className={styles.error}>{errors.firstName}</span>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    value={editableContact.lastName}
                    onChange={(e) =>
                      handleEditChange("lastName", e.target.value)
                    }
                    disabled={loading}
                  />
                  {errors.lastName && (
                    <span className={styles.error}>{errors.lastName}</span>
                  )}
                </div>
              </Row>

              <Row className={styles.splitGroup}>
                <div className={styles.inputGroup}>
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={editableContact.email}
                    onChange={(e) => handleEditChange("email", e.target.value)}
                    disabled={loading}
                  />
                  {errors.email && (
                    <span className={styles.error}>{errors.email}</span>
                  )}
                </div>
              </Row>
            </>
          )}
        </Section>

        {contact.los && (
          <Section>
            <header>
              <h3>Line of Sponsorship (LOS)</h3>
            </header>
            <Row>
              <Column width="15%">
                <LabelGroup
                  title={
                    "Pin Level" +
                    (contact.los.pinLevel.isPlugin ? " (PLUGIN)" : "")
                  }
                >
                  {contact.los.pinLevel.name}
                </LabelGroup>
              </Column>

              <Column width="20%">
                <LabelGroup title={"Sponsor"}>
                  <Link to={getMemberUrl(contact.los.sponsor.iboNumber)}>
                    {contact.los.sponsor.iboNumber} {contact.los.sponsor.name}
                  </Link>
                </LabelGroup>
              </Column>
              <Column width="20%">
                <LabelGroup title={"Platinum"}>
                  <Link to={getMemberUrl(contact.los.platinum.iboNumber)}>
                    {contact.los.platinum.iboNumber} {contact.los.platinum.name}
                  </Link>
                </LabelGroup>
              </Column>
              <Column width="20%">
                <LabelGroup title={"Emerald"}>
                  <Link to={getMemberUrl(contact.los.emerald.iboNumber)}>
                    {contact.los.emerald.iboNumber} {contact.los.emerald.name}
                  </Link>
                </LabelGroup>
              </Column>
              <Column width="20%">
                <LabelGroup title={"Diamond"}>
                  <Link to={getMemberUrl(contact.los.diamond.iboNumber)}>
                    {contact.los.diamond.iboNumber} {contact.los.diamond.name}
                  </Link>
                </LabelGroup>
              </Column>
            </Row>
          </Section>
        )}
      </Section>

      {orgPresent() && (
        <Section>
          {Object.keys(organizationTypes).map((orgType, i) => {
            let orgs = contact.topics[orgType];
            if (orgType === "ownOrganization") {
              orgs = [orgs];
            }

            let title = organizationTypes[orgType];

            return hasIndices(orgs) ? (
              <OrgList
                key={i}
                data={contact}
                orgs={orgs}
                title={title}
                orgType={orgType}
                userId={contact.topicsInfo.userId}
                setIsUsersOpen={setIsUsersOpen}
                setIsUsersGroup={setIsUsersGroup}
                setIsUsersHeader={setIsUsersHeader}
                retrieveGroupUsers={retrieveGroupUsers}
                retrieveThreadUsers={retrieveThreadUsers}
                loading={loading} // Pass loading
                setLoading={setLoading} // Pass setLoading
                isAccessOpen={isAccessOpen}
                setIsAccessOpen={setIsAccessOpen}
                clearAccessCode={clearAccessCode}
                accessCode={accessCode}
              />
            ) : (
              ""
            );
          })}
        </Section>
      )}
    </div>
  );
}

export function OrgList({
  data,
  orgs,
  title,
  orgType,
  userId,
  setIsUsersOpen,
  setIsUsersGroup,
  setIsUsersHeader,
  retrieveGroupUsers,
  retrieveThreadUsers,
  loading,
  setLoading,
  isAccessOpen,
  setIsAccessOpen,
  clearAccessCode,
  accessCode,
}) {
  const [contact, setContact] = useState(data);

  useEffect(() => {
    setContact(data);
  }, [data]);

  return (
    hasIndices(orgs) && (
      <>
        <header>
          <h3>{title}</h3>
        </header>
        {orgs.map((org, oi) => (
          <Fragment key={oi}>
            <div
              className={classNames(styles.tableContainer, {
                [styles.ownOrgTable]: orgType === "ownOrganization",
              })}
              role="table"
            >
              <div
                className={[styles.flexTable, styles.header].join(" ")}
                role="rowgroup"
              >
                {orgType !== "ownOrganization" && (
                  <div
                    className={[styles.flexRow, styles.first].join(" ")}
                    role="columnheader"
                  >
                    Upline
                  </div>
                )}
                <div
                  className={classNames(styles.flexRow, {
                    [styles.wideColumn]: orgType === "ownOrganization",
                  })}
                  role="columnheader"
                >
                  Groups / Threads
                </div>
                <div
                  className={classNames(styles.flexRow, {
                    [styles.wideColumn]: orgType === "ownOrganization",
                  })}
                  role="columnheader"
                >
                  Status
                </div>
              </div>

              <div className={styles.flexTable} role="rowgroup">
                {orgType !== "ownOrganization" && (
                  <div
                    className={[
                      styles.flexRow,
                      orgType === "ownOrganization"
                        ? styles.singleColumn
                        : styles.rowspan,
                      styles.first,
                    ].join(" ")}
                  >
                    <div>{org.name}</div>
                    {org.iboNumber !== 0 && (
                      <div className={styles.iboNumber}>
                        <Link to={getMemberUrl(org.iboNumber)}>
                          {org.iboNumber}
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.column}>
                  {/* Groups Section */}
                  {org.groups && org.groups.length > 0
                    ? org.groups.map((grp, gi) => (
                        <GroupEntry
                          key={gi}
                          data={contact}
                          title={title}
                          grp={grp}
                          isGroup={true}
                          userId={userId}
                          setIsUsersOpen={setIsUsersOpen}
                          setIsUsersGroup={setIsUsersGroup}
                          setIsUsersHeader={setIsUsersHeader}
                          retrieveGroupUsers={retrieveGroupUsers}
                          retrieveThreadUsers={retrieveThreadUsers}
                          loading={loading}
                          setLoading={setLoading}
                          isOwnOrganization={orgType === "ownOrganization"}
                          isAccessOpen={isAccessOpen}
                          setIsAccessOpen={setIsAccessOpen}
                          clearAccessCode={clearAccessCode}
                          accessCode={accessCode}
                        />
                      ))
                    : null}

                  {/* Threads Section */}
                  {org.threads && org.threads.length > 0
                    ? org.threads.map((thread, ti) => (
                        <GroupEntry
                          key={ti}
                          data={contact}
                          title={title}
                          grp={thread}
                          isGroup={false}
                          userId={userId}
                          setIsUsersOpen={setIsUsersOpen}
                          setIsUsersGroup={setIsUsersGroup}
                          setIsUsersHeader={setIsUsersHeader}
                          retrieveGroupUsers={retrieveGroupUsers}
                          retrieveThreadUsers={retrieveThreadUsers}
                          loading={loading}
                          setLoading={setLoading}
                          isOwnOrganization={orgType === "ownOrganization"}
                          isAccessOpen={isAccessOpen}
                          setIsAccessOpen={setIsAccessOpen}
                          clearAccessCode={clearAccessCode}
                          accessCode={accessCode}
                        />
                      ))
                    : null}

                  {/* Show "Added to Contacts" row only once if there are no groups and no threads */}
                  {!org.groups?.length && !org.threads?.length && (
                    <div className={styles.flexRow} role="row">
                      {orgType !== "ownOrganization" && (
                        <div className={styles.flexCell}>Added to Contacts</div>
                      )}
                      <div className={styles.flexCell}>
                        {org.deletedOn
                          ? `Deleted on ${dayjs(org.deletedOn)
                              .tz("America/Los_Angeles")
                              .format("MM/DD/YY HH:mm:ss A")} PT`
                          : org.addedOn
                          ? `Added on ${dayjs(org.addedOn)
                              .tz("America/Los_Angeles")
                              .format("MM/DD/YY HH:mm:ss A")} PT`
                          : "No Add Date"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Fragment>
        ))}
      </>
    )
  );
}

export function GroupEntry({
  data,
  title,
  grp,
  isGroup,
  userId,
  setIsUsersOpen,
  setIsUsersGroup,
  setIsUsersHeader,
  retrieveGroupUsers,
  retrieveThreadUsers,
  loading,
  setLoading,
  isOwnOrganization, // Accept isOwnOrganization prop
  isAccessOpen,
  setIsAccessOpen,
  clearAccessCode,
  accessCode,
}) {
  const [group, setGroup] = useState(grp);
  const [contact, setContact] = useState(data);

  useEffect(() => {
    setContact(data);
  }, [data]);

  useEffect(() => {
    setGroup(grp);
  }, [grp]);

  return (
    <>
      <ReactModal
        isOpen={isAccessOpen}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnOverlayEsc={true}
        onRequestClose={() => {
          setIsAccessOpen(false);
        }}
      >
        <AccessCodeModal
          onClose={() => {
            clearAccessCode();
            setIsAccessOpen(false);
          }}
          accessCode={accessCode}
        ></AccessCodeModal>
      </ReactModal>

      <div className={styles.flexRow}>
        <div className={styles.flexCell} role="cell">
          <div className={styles.groupRow}>
            <div className={styles.groupLink}>
              <div className={styles.groupName}>
                {group.groupName ?? group.threadName}
                <br />
                {!isOwnOrganization && <div> Added to Contacts</div>}
              </div>
            </div>
            {isOwnOrganization && ( // Render button only for ownOrganization
              <div className={styles.syncBtn}>
                <Button
                  size="small"
                  onClick={async () => {
                    setLoading(true); // Start loader
                    try {
                      setIsUsersOpen(true);
                      setIsUsersGroup(isGroup);
                      setIsUsersHeader(group.groupName ?? group.threadName);

                      if (isGroup) {
                        await retrieveGroupUsers(userId, group.groupId);
                      } else {
                        await retrieveThreadUsers(userId, group.threadId);
                      }
                    } catch (error) {
                      console.error("Error fetching data: ", error);
                    } finally {
                      setLoading(false); // Stop loader
                    }
                  }}
                >
                  View {isGroup ? "Group" : "Thread"} Members
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.flexCell} role="cell">
          {group.deletedOn
            ? `Deleted on ${dayjs(group.deletedOn)
                .tz("America/Los_Angeles")
                .format("MM/DD/YY HH:mm:ss A")} PT`
            : group.addedOn == null
            ? "Not Created"
            : group.verifiedOn == null && title !== "Events"
            ? "Added (Unverified)"
            : group.addedOn
            ? "Added on " +
              dayjs(group.addedOn)
                .tz("America/Los_Angeles")
                .format("MM/DD/YY HH:mm:ss A") +
              " PT"
            : ""}
        </div>
      </div>
    </>
  );
}
